import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { ToastrService } from "ngx-toastr";
import { CommonService } from "../../services/common.service";
import { RedistributionStateService } from "./redistribution-state.service";
// Enhancement (eligibility gate): rule-engine detection uses the same API as Create.
import { RuleEngineService } from "../../services/rule-engine.service";

declare const copyToClipboard: (text: string) => void;

/**
 * Fallback when API returns Failure with empty statusmessage (UI-guarded or generic server error).
 * Data-dependent summaries still come from API statusmessage via resolveRedistributionApiMessage.
 */
const REDISTRIBUTION_API_MESSAGE_FALLBACKS = {
  validationFailed: "Validation failed.",
  urlLookupFailed: "URL lookup failed.",
  redistributionFailed:
    "Redistribution failed. Please try again.",
  proceedNoValidation:
    "Cannot verify selected pipelines without validation results.",
  rowValidationFailed: "Validation failed for this ID.",
} as const;

/** Prefer non-empty API statusmessage; otherwise use the provided UI fallback. */
function resolveRedistributionApiMessage(
  data: Record<string, unknown> | null | undefined,
  fallback: string,
): string {
  const statusMsg = String(data?.["statusmessage"] ?? "").trim();
  if (statusMsg) {
    return statusMsg;
  }
  return fallback;
}

/** One pipeline row in the Proceed table (Validate API / SP list). */
type ProceedPipeline = {
  id: string;
  name: string;
  source?: string;
  target?: string;
  messageType?: string;
  status?: string;
};

/**
 * One Case/Claim ID shown in the pipeline-grouped table IDs column.
 * Hard-invalid IDs (no SP pipelines) are listed on every pipeline row for optional force selection.
 */
type PipelineGroupedIdBinding = {
  objectId: string;
  valid: boolean;
  requiresProceed: boolean;
  /** true when Validate returned no pipelines for this ID (red status row). */
  isHardInvalid: boolean;
};

/** Pipeline-centric row: one pipeline with the IDs that map to it (enhanced Card 2 layout). */
type PipelineGroupRow = {
  pipeline: ProceedPipeline;
  ids: PipelineGroupedIdBinding[];
};

/** One row in post-redistribute success table (Case/Claim ID × pipeline × transaction id). */
type RedistributionSuccessItem = {
  caseOrClaimId: string;
  pipelineName: string;
  transactionId: string;
  /** Route GUID — used to mark autocomplete-selected pipeline vs Proceed-listed pipelines. */
  pipelineId?: string;
  /** True for the pipeline chosen in the search box (input pipeline). */
  isInputPipeline?: boolean;
};

/**
 * POST api/ReDistribution/GetReDistributionUrl completes the flow (ReDistributionController).
 * The legacy POST api/Redistribution/Execute step is not invoked from this screen.
 */
@Component({
  selector: "app-logs-redistribution",
  templateUrl: "./log-redistribution.component.html",
  styleUrls: ["./log-redistribution.component.scss"],
})
export class LogsRedistributionComponent implements OnInit, OnDestroy {
  form: FormGroup;
  keywordPipeline = "displayname";
  routeList: unknown[] = [];
  isLoadingRoutes = false;
  notfoundRoutes = false;
  selectedRouteId = "";
  selectedRouteLabel = "";
  /** Normalized from api/Routes/get (trimmed). */
  messageType = "";
  deploymentKey = "";
  pipelineselectclass = "";

  /** Sender/source agent GUID from selected pipeline (api/Routes/get — same rules as Create; sent in POST only). */
  sourceAgentId = "";

  /** Routedefinition status from api/Routes/get (e.g. active / passive). */
  selectedRouteStatus = "";

  private static readonly MAX_COMMA_SEPARATED_IDS = 10;

  private readonly toastOpts = {
    timeOut: 4000,
    positionClass: "toast-bottom-right" as const,
  };

  private readonly toastLong = {
    timeOut: 8000,
    positionClass: "toast-bottom-right" as const,
  };

  successItems: RedistributionSuccessItem[] = [];

  /** Per-ID validation rows shown in Card 2 (green / red / proceed-warning). */
  validationResults: {
    id: string;
    valid: boolean;
    message?: string;
    source?: string;
    requiresProceed?: boolean;
    pipelines?: ProceedPipeline[];
  }[] = [];

  /** Checked SP pipeline GUIDs per Case/Claim ID — sent on Redistribute (Proceed path). */
  selectedProceedPipelineIds: Record<string, string[]> = {};

  /** Id → display name snapshot before validation UI is cleared (fallback when API omits PipelineName). */
  private pipelineDisplayNameById: Record<string, string> = {};

  /** True when last Validate call returned allValid for current snapshot. */
  validationPassed = false;

  /** True when SP listed other pipelines — Redistribute may run on the warning path after Validate. */
  proceedAvailable = false;

  /** Serialized pipeline + IDs; must match for Redistribute to stay enabled. */
  private validationSnapshot = "";

  isValidating = false;

  showReasonModal = false;
  /** True while the redistribute API call is in-flight (keeps validation table hidden behind loader). */
  isRedistributing = false;
  redistributionReason = "";
  private pendingPayload: Record<string, unknown> | null = null;

  // ---------------------------------------------------------------------------
  // Enhancement (eligibility gate): redistribution allowed only for Outbound
  // AND rule-engine interfaces. Pipeline can still be selected; Validate /
  // Redistribute are disabled with a message when not eligible.
  // ---------------------------------------------------------------------------
  /** INTERFACETYPE from api/Routes/get (e.g. outbound / inbound). */
  interfaceDirection = "";
  /** True when GetRuleConfigVersions returns versions (same rule as Create). */
  isRuleEngine = false;
  /** True while the async rule-engine check is in-flight (buttons stay disabled). */
  isCheckingRuleEngine = false;
  /** Route UniqueID needed for the rule-engine versions lookup. */
  private selectedRouteUniqueId = "";
  /** One-shot guard: max-ID toast fires once per cap hit, resets when count drops below max. */
  private maxIdsLimitToastShown = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly commonService: CommonService,
    private readonly ngxLoader: NgxUiLoaderService,
    private readonly toastr: ToastrService,
    private readonly redistributionState: RedistributionStateService,
    // Enhancement: injected to detect rule-engine interfaces (eligibility gate).
    private readonly ruleEngineService: RuleEngineService,
  ) {
    this.form = this.fb.group({
      interfaceSearch: [""],
      caseId: [""],
      claimId: [""],
    });
  }

  ngOnInit(): void {
    const saved = this.redistributionState.getState();
    if (!saved) {
      return;
    }
    this.form.patchValue(
      {
        interfaceSearch: saved.interfaceSearchValue,
        caseId: saved.caseIdValue,
        claimId: saved.claimIdValue,
      },
      { emitEvent: false },
    );
    this.selectedRouteId = saved.selectedRouteId;
    this.selectedRouteLabel = saved.selectedRouteLabel;
    this.messageType = saved.messageType;
    this.deploymentKey = saved.deploymentKey;
    this.pipelineselectclass = saved.pipelineselectclass;
    this.sourceAgentId = saved.sourceAgentId ?? "";
  }

  ngOnDestroy(): void {
    this.redistributionState.saveState({
      interfaceSearchValue: this.form.get("interfaceSearch")?.value ?? null,
      caseIdValue: String(this.form.get("caseId")?.value ?? ""),
      claimIdValue: String(this.form.get("claimId")?.value ?? ""),
      selectedRouteId: this.selectedRouteId,
      selectedRouteLabel: this.selectedRouteLabel,
      messageType: this.messageType,
      deploymentKey: this.deploymentKey,
      pipelineselectclass: this.pipelineselectclass,
      sourceAgentId: this.sourceAgentId,
    });
  }

  /** True when route message type is dft or vcdft (case-insensitive). */
  isClaimIdMode(): boolean {
    const u = (this.messageType || "").trim().toLowerCase();
    return u === "dft" || u === "vcdft";
  }

  /** Show Case ID / Claim ID row only after a pipeline is selected and details loaded. */
  showIdRow(): boolean {
    return !!this.selectedRouteId && !!this.messageType;
  }

  /** Label for Case ID vs Claim ID rows in the messages card. */
  getIdLabel(): "Case ID" | "Claim ID" {
    return this.isClaimIdMode() ? "Claim ID" : "Case ID";
  }

  /** True when selected pipeline status is active (empty status allowed for legacy routes). */
  private isSelectedPipelineActive(): boolean {
    const status = (this.selectedRouteStatus ?? "").trim().toLowerCase();
    return !status || status === "active";
  }

  /**
   * Blocks Validate / Redistribute when autocomplete pipeline is passive/inactive.
   * @returns true when caller should stop (toast already shown).
   */
  private warnIfSelectedPipelineInactive(): boolean {
    if (!this.selectedRouteId?.trim() || this.isSelectedPipelineActive()) {
      return false;
    }
    this.toastr.warning(
      "The interface is inactive. You cannot redistribute. To redistribute, make the interface active first.",
      "",
      this.toastLong,
    );
    return true;
  }

  /** Redistribute enabled after strict Validate pass or proceed path (pipeline not in SP list), for unchanged pipeline + IDs. */
  canRedistribute(): boolean {
    const snapshotOk =
      this.validationSnapshot !== "" &&
      this.validationSnapshot === this.buildValidationSnapshot();
    const unlocked =
      this.validationPassed ||
      this.proceedAvailable ||
      // Enhancement: unlock when user checked pipeline(s) for hard-invalid ID(s).
      this.hasExplicitPipelineSelections() ||
      // Enhancement: hard-invalid on input pipeline only (locked ✓) — allow publish; rule engine qualifies downstream.
      this.hasHardInvalidValidationRows();
      // Align-with-previous block (commented): hard-invalid did not unlock Redistribute.
      // this.proceedAvailable only;
    // Enhancement: also require Outbound + rule-engine eligibility.
    return unlocked && snapshotOk && this.isInterfaceEligible();
  }

  /** Clears validation state when pipeline or IDs change (auto-invalidate). */
  private clearValidationState(): void {
    this.validationResults = [];
    this.validationPassed = false;
    this.proceedAvailable = false;
    this.validationSnapshot = "";
    this.selectedProceedPipelineIds = {};
    this.pipelineDisplayNameById = {};
  }

  /** Fingerprint of inputs that must match after Validate before Redistribute. */
  private buildValidationSnapshot(): string {
    const claimMode = this.isClaimIdMode();
    const raw = claimMode
      ? String(this.form.get("claimId")?.value ?? "")
      : String(this.form.get("caseId")?.value ?? "");
    return `${this.selectedRouteId}|${this.messageType}|${this.sanitizeNumericIdsInput(raw)}`;
  }

  onInterfaceInputChanged(val: string): void {
    if (!val || String(val).length < 3) {
      return;
    }
    this.isLoadingRoutes = true;
    const url =
      "api/Routes/GetByDisplayName?name=" +
      encodeURIComponent(val) +
      "&version=2";
    this.commonService.getapiurl(url).subscribe({
      next: (data: unknown) => {
        this.isLoadingRoutes = false;
        const list = Array.isArray(data) ? data : [];
        this.routeList = list.map((obj: Record<string, unknown>) => ({
          ...obj,
          displayname: `${obj["displayname"]} (${obj["id"]})(VA-${obj["UniqueID"]})`,
        })) as unknown[];
        this.notfoundRoutes = this.routeList.length === 0;
      },
      error: () => {
        this.isLoadingRoutes = false;
        this.routeList = [];
        this.notfoundRoutes = true;
        this.toastr.error(
          "Unable to fetch the Pipelines",
          "",
          this.toastOpts,
        );
      },
    });
  }

  /**
   * Input shows pipeline name only (same as Create); GUID is copied via the copy icon.
   */
  private formatPipelineInputLabel(item: Record<string, unknown>): string {
    let label = String(item?.["displayname"] ?? "").trim();
    // Dropdown row is "Name (guid)(VA-n)" — strip suffixes so only the name appears in the input.
    const nameOnly = label.match(/^(.+?)\s*\([0-9a-f-]{36}\)/i);
    if (nameOnly) {
      return nameOnly[1].trim();
    }
    return label.replace(/\(VA-[^)]*\)\s*$/i, "").trim();
  }

  /** Hover title on pipeline input — name + GUID when a pipeline is selected (like Create). */
  getPipelineInputTitle(): string {
    if (!this.selectedRouteId) {
      return "";
    }
    const name = String(this.selectedRouteLabel ?? "").trim();
    return name ? `${name} (${this.selectedRouteId})` : this.selectedRouteId;
  }

  selectedInterface(item: Record<string, unknown>): void {
    const id = item?.["id"];
    if (!id) {
      return;
    }
    this.pipelineselectclass = "ng-autocompleteselect";
    this.selectedRouteId = String(id);
    this.selectedRouteLabel = this.formatPipelineInputLabel(item);
    this.form.patchValue({ interfaceSearch: this.selectedRouteLabel });
    this.clearValidationState();
    this.loadRouteDetails(String(id));
  }

  clearInterfaceSearch(): void {
    this.pipelineselectclass = "";
    this.form.patchValue({ interfaceSearch: "", caseId: "", claimId: "" });
    this.selectedRouteId = "";
    this.selectedRouteLabel = "";
    this.messageType = "";
    this.deploymentKey = "";
    this.routeList = [];
    this.sourceAgentId = "";
    this.selectedRouteStatus = "";
    // Enhancement: reset eligibility state when the pipeline selection is cleared.
    this.interfaceDirection = "";
    this.isRuleEngine = false;
    this.isCheckingRuleEngine = false;
    this.selectedRouteUniqueId = "";
    this.maxIdsLimitToastShown = false;
    this.clearValidationState();
  }

  interfaceSearchCleared(): void {
    this.clearInterfaceSearch();
  }

  /** Shared clipboard helper for pipeline GUID, SP pipeline id, and transaction id copy icons. */
  copyText(text: string, toastMessage: string): void {
    const value = String(text ?? "").trim();
    if (!value) {
      return;
    }
    copyToClipboard(value);
    this.toastr.success(toastMessage, "", {
      timeOut: 500,
      positionClass: "toast-bottom-right",
    });
  }

  copyInterfaceId(): void {
    this.copyText(this.selectedRouteId, "Interface GUID Copied");
  }

  /** Copy transaction id from post-redistribute success row. */
  copyTransactionId(transactionId: string): void {
    this.copyText(transactionId, "Transaction ID Copied");
  }

  /** Bulk copy all transaction IDs from success table (comma-separated). */
  copyAllTransactionIds(): void {
    const ids = this.successItems
      .map((x) => String(x.transactionId ?? "").trim())
      .filter((id) => id.length > 0);
    if (ids.length === 0) {
      return;
    }
    this.copyText(ids.join(","), `${ids.length} transaction ID(s) copied`);
  }

  /** Format success-row pipeline for clipboard: "name (guid)" or name only when GUID absent. */
  private formatPipelineCopyText(item: RedistributionSuccessItem): string {
    const name = String(item.pipelineName ?? "").trim();
    const guid = String(item.pipelineId ?? "").trim();
    if (!name) {
      return guid;
    }
    return guid ? `${name} (${guid})` : name;
  }

  /** Copy one pipeline from post-redistribute success row (name + GUID). */
  copyPipelineName(item: RedistributionSuccessItem): void {
    const text = this.formatPipelineCopyText(item);
    if (!text) {
      return;
    }
    this.copyText(text, "Pipeline copied");
  }

  /** Bulk copy all pipelines from success table (name + GUID, one per line). */
  copyAllPipelineNames(): void {
    const entries = this.successItems
      .map((x) => this.formatPipelineCopyText(x))
      .filter((s) => s.length > 0);
    if (entries.length === 0) {
      return;
    }
    this.copyText(entries.join("\n"), `${entries.length} pipeline(s) copied`);
  }

  /** Dedupe SP pipelines by id so the list is easier to scan in Card 2. */
  getUniquePipelines(
    pipelines: ProceedPipeline[] | undefined,
  ): ProceedPipeline[] {
    const seen = new Set<string>();
    const unique: ProceedPipeline[] = [];
    for (const p of pipelines ?? []) {
      const id = String(p?.id ?? "").trim();
      if (!id) {
        continue;
      }
      const key = id.toLowerCase();
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      unique.push({
        id,
        name: String(p?.name ?? "").trim(),
        source: String(p?.source ?? "").trim() || undefined,
        target: String(p?.target ?? "").trim() || undefined,
        messageType: String(p?.messageType ?? "").trim() || undefined,
        status: String(p?.status ?? "").trim() || undefined,
      });
    }
    return unique;
  }

  /** True when pipeline GUID matches the autocomplete-selected input pipeline. */
  isInputPipelineId(pipelineId: string | undefined): boolean {
    const pid = String(pipelineId ?? "").trim().toLowerCase();
    const selected = this.selectedRouteId?.trim().toLowerCase() ?? "";
    return pid.length > 0 && selected.length > 0 && pid === selected;
  }

  /** Proceed table: active pipelines only (backend may already filter; keeps UI safe for legacy responses). */
  getActiveProceedPipelines(
    pipelines: ProceedPipeline[] | undefined,
  ): ProceedPipeline[] {
    return this.getUniquePipelines(pipelines).filter((p) => {
      const status = (p.status ?? "").trim().toLowerCase();
      return !status || status === "active";
    });
  }

  /** Stable row key for Proceed pipeline *ngFor (avoids checkbox DOM reset on each CD pass). */
  trackProceedPipelineById(_index: number, pipeline: ProceedPipeline): string {
    return pipeline.id;
  }

  /**
   * Toggle on click — reads next state explicitly so [checked] stays in sync after *ngFor refresh.
   */
  onProceedPipelineToggle(objectId: string, pipelineId: string): void {
    const nextChecked = !this.isProceedPipelineChecked(objectId, pipelineId);
    this.onProceedPipelineCheck(objectId, pipelineId, nextChecked);
  }

  /** Toggle checkbox for an SP-listed pipeline under a given Case/Claim ID. */
  onProceedPipelineCheck(
    objectId: string,
    pipelineId: string,
    checked: boolean,
  ): void {
    const key = String(objectId ?? "").trim();
    const id = String(pipelineId ?? "").trim();
    if (!key || !id) {
      return;
    }
    const current = new Set(this.selectedProceedPipelineIds[key] ?? []);
    if (checked) {
      current.add(id);
    } else {
      current.delete(id);
    }
    // Replace whole map so change detection sees the update (nested assign alone could leave boxes stuck).
    this.selectedProceedPipelineIds = {
      ...this.selectedProceedPipelineIds,
      [key]: [...current],
    };
  }

  isProceedPipelineChecked(objectId: string, pipelineId: string): boolean {
    const key = String(objectId ?? "").trim();
    const id = String(pipelineId ?? "").trim().toLowerCase();
    return (this.selectedProceedPipelineIds[key] ?? []).some(
      (x) => x.trim().toLowerCase() === id,
    );
  }

  /** Build payload for checked pipelines (additional SP-listed pipelines per Case/Claim ID). */
  private buildSelectedPipelineIdsByObjectId():
    | Record<string, string[]>
    | undefined {
    // Valid, proceed, or hard-invalid force-select: send checked additional pipelines (input pipeline always sent separately).
    if (
      !this.validationPassed &&
      !this.proceedAvailable &&
      !this.hasExplicitPipelineSelections()
    ) {
      return undefined;
    }
    const out: Record<string, string[]> = {};
    for (const [objectId, pipelineIds] of Object.entries(
      this.selectedProceedPipelineIds,
    )) {
      const cleaned = (pipelineIds ?? [])
        .map((x) => String(x).trim())
        .filter((x) => x.length > 0);
      if (cleaned.length > 0) {
        out[objectId] = cleaned;
      }
    }
    return Object.keys(out).length > 0 ? out : undefined;
  }

  private loadRouteDetails(routeId: string): void {
    const url = "api/Routes/get?id=" + encodeURIComponent(routeId);
    this.ngxLoader.start();
    this.commonService.getapiurl(url).subscribe({
      next: (data: any) => {
        const row = data as Record<string, unknown>;
        this.ngxLoader.stop();
        const prev = this.messageType;
        this.messageType = String(row?.["messagetype"] ?? "").trim();
        this.deploymentKey = this.resolveDeploymentKey(row);
        this.selectedRouteStatus = String(
          row?.["status"] ?? row?.["STATUS"] ?? "",
        ).trim();
        this.applySourceAgentFromRoute(row);
        // Enhancement: capture interface direction + UniqueID, then resolve rule-engine eligibility.
        this.interfaceDirection = String(
          row?.["INTERFACETYPE"] ?? row?.["interfacetype"] ?? "",
        ).trim();
        this.selectedRouteUniqueId = String(
          row?.["UniqueID"] ?? row?.["uniqueID"] ?? row?.["uniqueid"] ?? "",
        ).trim();
        this.resolveRuleEngine(this.selectedRouteUniqueId);
        if (!this.deploymentKey) {
          this.toastr.warning(
            "No deployment key found for this pipeline. Redistribution may fail until deployment is configured.",
            "",
            this.toastOpts,
          );
        }
        if (prev !== this.messageType) {
          this.form.patchValue({ caseId: "", claimId: "" });
          this.clearValidationState();
        }
      },
      error: () => {
        this.ngxLoader.stop();
        this.messageType = "";
        this.deploymentKey = "";
        this.sourceAgentId = "";
        this.selectedRouteStatus = "";
        // Enhancement: reset eligibility state on load failure.
        this.interfaceDirection = "";
        this.isRuleEngine = false;
        this.isCheckingRuleEngine = false;
        this.selectedRouteUniqueId = "";
        this.toastr.error(
          "Unable to load pipeline details.",
          "",
          this.toastOpts,
        );
      },
    });
  }

  /**
   * Enhancement: resolve rule-engine flag (same approach as create.component.ts).
   * GetRuleConfigVersions returns versions only for rule-engine interfaces.
   */
  private resolveRuleEngine(uniqueId: string): void {
    this.isRuleEngine = false;
    if (!uniqueId) {
      return;
    }
    this.isCheckingRuleEngine = true;
    this.ruleEngineService.GetRuleConfigVersions(uniqueId, "Validation").subscribe({
      next: (versions: unknown) => {
        this.isRuleEngine = Array.isArray(versions) && versions.length > 0;
        this.isCheckingRuleEngine = false;
      },
      error: () => {
        this.isRuleEngine = false;
        this.isCheckingRuleEngine = false;
      },
    });
  }

  /** Enhancement: true when selected interface direction is outbound. */
  isOutboundInterface(): boolean {
    return (this.interfaceDirection || "").trim().toLowerCase() === "outbound";
  }

  /** Enhancement: redistribution allowed only for Outbound AND rule-engine interfaces. */
  isInterfaceEligible(): boolean {
    return this.isOutboundInterface() && this.isRuleEngine;
  }

  /** Enhancement: message explaining why the selected interface is not eligible. */
  getInterfaceIneligibleMessage(): string {
    if (!this.selectedRouteId) {
      return "";
    }
    // Async rule-engine check in-flight — avoid flashing the combined ineligible message.
    if (this.isCheckingRuleEngine) {
      return "Checking interface eligibility…";
    }
    // Single combined message for any ineligible case (not outbound, not rule-engine, or both).
    if (!this.isInterfaceEligible()) {
      return "Redistribution is allowed only for Outbound rule-engine interfaces.";
    }
  /* Granular messages (commented): replaced by isInterfaceEligible() + combined text above.
    if (!this.isOutboundInterface() && !this.isRuleEngine) {
      return "Redistribution is allowed only for Outbound rule-engine interfaces.";
    }
    if (!this.isOutboundInterface()) {
      return "Redistribution is allowed only for Outbound interfaces.";
    }
    if (!this.isRuleEngine) {
      return "Redistribution is allowed only for rule-engine interfaces.";
    }
  */
    return "";
  }

  /**
   * Same rules as Create pipeline (`create.component.ts` getRouteDetails):
   * if `senderapikey` is set, use `senderID` + `senderdisplayname` for display;
   * otherwise fall back to `sourceagentid` on the route.
   */
  private applySourceAgentFromRoute(data: Record<string, unknown>): void {
    this.sourceAgentId = "";
    const senderApiKey =
      data["senderapikey"] ??
      data["Senderapikey"] ??
      data["SenderApiKey"] ??
      data["senderApiKey"];
    const hasSenderKey =
      senderApiKey != null && String(senderApiKey).trim() !== "";
    const senderId = String(
      data["senderID"] ??
        data["senderId"] ??
        data["SenderID"] ??
        "",
    ).trim();

    if (hasSenderKey && senderId) {
      this.sourceAgentId = senderId;
      return;
    }

    const proxyId = String(
      data["sourceagentid"] ??
        data["Sourceagentid"] ??
        data["SourceAgentId"] ??
        "",
    ).trim();
    if (proxyId) {
      this.sourceAgentId = proxyId;
    }
  }

  private resolveDeploymentKey(data: Record<string, unknown>): string {
    const iface = String(
      data?.["INTERFACETYPE"] ?? data?.["interfacetype"] ?? "",
    );
    const se = String(
      data?.["SEDEPLOYMENTKEY"] ?? data?.["sedepploymentkey"] ?? "",
    );
    const de = String(
      data?.["DEDEPLOYMENTKEY"] ?? data?.["dedepploymentkey"] ?? "",
    );
    const outbound = iface.trim().toLowerCase() === "outbound";
    if (outbound && de.trim()) {
      return de.trim();
    }
    if (!outbound && se.trim()) {
      return se.trim();
    }
    return (de || se || "").trim();
  }

  /**
   * Allow only digits and commas; cap at MAX comma-separated IDs (11th ID cannot be entered).
   * Shows a one-shot toast when the user attempts more than the max (sanitize truncates silently).
   */
  onNumericIdsInput(ev: Event, controlName: "caseId" | "claimId"): void {
    const el = ev.target as HTMLInputElement;
    const raw = el.value;
    const max = LogsRedistributionComponent.MAX_COMMA_SEPARATED_IDS;
    const attemptedCount = this.getAttemptedUniqueIdCount(raw);
    const next = this.sanitizeNumericIdsInput(raw);
    if (next !== el.value) {
      el.value = next;
      this.form.get(controlName)?.setValue(next, { emitEvent: false });
    }
    // Notify once when input is capped; reset when user drops below max (e.g. deletes an ID).
    if (attemptedCount > max) {
      if (!this.maxIdsLimitToastShown) {
        this.maxIdsLimitToastShown = true;
        const plural = this.getIdLabel() === "Case ID" ? "Case IDs" : "Claim IDs";
        this.toastr.warning(
          `At most ${max} ${plural} are allowed.`,
          "",
          this.toastOpts,
        );
      }
    } else if (this.getAttemptedUniqueIdCount(next) < max) {
      this.maxIdsLimitToastShown = false;
    }
    // Any ID edit invalidates prior Validate until user clicks Validate again.
    this.clearValidationState();
  }

  /** Unique numeric ID count in raw field text (before sanitize cap) — for max-limit toast only. */
  private getAttemptedUniqueIdCount(raw: string): number {
    const cleaned = String(raw ?? "").replace(/[^\d,]/g, "");
    const tokens = cleaned
      .split(",")
      .map((s) => s.replace(/\D/g, ""))
      .filter((s) => s.length > 0);
    return this.dedupeIdTokens(tokens).unique.length;
  }

  /**
   * Strips non-numeric characters (except comma separators). At most 10 unique numeric IDs;
   * duplicate Case/Claim IDs are rejected while typing (not only on Validate).
   */
  private sanitizeNumericIdsInput(raw: string): string {
    const MAX = LogsRedistributionComponent.MAX_COMMA_SEPARATED_IDS;
    const cleaned = String(raw ?? "").replace(/[^\d,]/g, "");
    const segments = cleaned.split(",");
    const completed: string[] = [];

    const isDuplicate = (id: string): boolean =>
      completed.some((existing) => existing === id);

    for (let i = 0; i < segments.length; i++) {
      const digits = segments[i].replace(/\D/g, "");
      const isLast = i === segments.length - 1;

      if (isLast) {
        const prefix = completed.join(",");
        if (completed.length >= MAX) {
          return prefix;
        }
        if (digits.length === 0) {
          return prefix + (completed.length > 0 ? "," : "");
        }
        // Block duplicate ID in the trailing segment (e.g. paste or typing same ID again).
        if (isDuplicate(digits)) {
          return prefix;
        }
        if (completed.length < MAX) {
          return prefix + (prefix ? "," : "") + digits;
        }
        return prefix;
      }

      if (digits.length > 0 && completed.length < MAX && !isDuplicate(digits)) {
        completed.push(digits);
      }
    }

    return completed.join(",");
  }

  /**
   * Keeps first occurrence of each numeric ID when user enters duplicates (e.g. 123,123,456).
   */
  private dedupeIdTokens(tokens: string[]): {
    unique: string[];
    hadDuplicates: boolean;
  } {
    const seen = new Set<string>();
    const unique: string[] = [];
    for (const t of tokens) {
      if (seen.has(t)) {
        continue;
      }
      seen.add(t);
      unique.push(t);
    }
    return { unique, hadDuplicates: unique.length < tokens.length };
  }

  /** Writes deduped comma-separated IDs back to the form field for display. */
  private applyDedupedIdsToField(
    controlName: "caseId" | "claimId",
    ids: string[],
  ): string {
    const joined = ids.join(",");
    this.form.get(controlName)?.setValue(joined, { emitEvent: false });
    return joined;
  }

  /**
   * Split comma-separated numeric IDs: validate digits-only, max 10 unique IDs (UI should already enforce).
   */
  private parseCommaSeparatedIds(
    raw: string,
    label: "Case ID" | "Claim ID",
  ): string[] | null {
    const sanitized = this.sanitizeNumericIdsInput(String(raw ?? ""));
    const tokens = sanitized
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    for (const t of tokens) {
      if (!/^\d+$/.test(t)) {
        this.toastr.warning(
          label === "Case ID"
            ? "Case IDs must contain digits only."
            : "Claim IDs must contain digits only.",
          "",
          this.toastOpts,
        );
        return null;
      }
    }

    // Remove duplicate Case/Claim IDs; first occurrence wins (same rule as pipeline list dedupe).
    const { unique, hadDuplicates } = this.dedupeIdTokens(tokens);
    if (hadDuplicates) {
      const plural = label === "Case ID" ? "Case IDs" : "Claim IDs";
      this.toastr.info(`Duplicate ${plural} were removed.`, "", this.toastOpts);
    }

    if (unique.length > LogsRedistributionComponent.MAX_COMMA_SEPARATED_IDS) {
      const plural = label === "Case ID" ? "Case IDs" : "Claim IDs";
      this.toastr.warning(
        `At most 10 ${plural} are allowed.`,
        "",
        this.toastOpts,
      );
      return null;
    }

    return unique;
  }

  /** Builds POST body for ValidateIds (deployment not required per requirements). */
  private buildValidatePayload(): Record<string, unknown> | null {
    if (!this.selectedRouteId) {
      this.toastr.warning(
        "Select a pipeline before validating.",
        "",
        this.toastOpts,
      );
      return null;
    }

    // sourceAgentId is resolved from api/Routes/get when pipeline is selected (not computed on server).
    if (!this.sourceAgentId?.trim()) {
      this.toastr.warning(
        "Source agent is not configured for this pipeline. Cannot validate.",
        "",
        this.toastOpts,
      );
      return null;
    }

    const claimMode = this.isClaimIdMode();
    const rawField = this.sanitizeNumericIdsInput(
      claimMode
        ? String(this.form.get("claimId")?.value ?? "")
        : String(this.form.get("caseId")?.value ?? ""),
    );
    const controlName = claimMode ? "claimId" : "caseId";
    this.form.get(controlName)?.setValue(rawField, { emitEvent: false });
    const label: "Case ID" | "Claim ID" = claimMode ? "Claim ID" : "Case ID";
    const ids = this.parseCommaSeparatedIds(rawField, label);
    if (ids === null) {
      return null;
    }
    if (ids.length === 0) {
      this.toastr.warning(
        claimMode ? "Claim ID is required." : "Case ID is required.",
        "",
        this.toastOpts,
      );
      return null;
    }

    const idField = this.applyDedupedIdsToField(controlName, ids);

    const sessionRaw = sessionStorage.getItem("sessionObjectlogin");
    const loggedInUser: string = sessionRaw
      ? String(JSON.parse(sessionRaw)?.userdata?.["userid"] ?? "")
      : "";

    const distribution: Array<{ caseId: string } | { claimId: string }> = claimMode
      ? ids.map((id) => ({ claimId: id }))
      : ids.map((id) => ({ caseId: id }));

    return {
      interfaceId: this.selectedRouteId,
      distribution,
      caseId: claimMode ? "" : idField,
      claimId: claimMode ? idField : "",
      messageType: this.messageType,
      username: loggedInUser,
      sourceAgentId: this.sourceAgentId.trim(),
    };
  }

  /** Calls api/ReDistribution/ValidateIds and updates Card 2 per-ID status. */
  onValidate(): void {
    if (this.warnIfSelectedPipelineInactive()) {
      return;
    }
    const payload = this.buildValidatePayload();
    if (!payload) {
      return;
    }

    this.isValidating = true;
    this.successItems = [];
    // Close pipeline autocomplete overlay (z-index 10050) so Card 2 checkboxes receive clicks.
    this.routeList = [];
    this.commonService.postRedistributionValidateIds(payload).subscribe({
      next: (data: Record<string, unknown>) => {
        this.isValidating = false;
        this.applyValidationFromResponse(data);
        const summary = String(data?.["statusmessage"] ?? "").trim();
        // Snapshot when strict-valid or Proceed path is available (same pipeline + IDs).
        // Existing: snapshot when strict-valid or proceed path unlocks Redistribute.
        if (this.validationPassed || this.proceedAvailable) {
          this.validationSnapshot = this.buildValidationSnapshot();
        } else if (this.validationResults.length > 0) {
          // Enhancement: hard-invalid-only Validate still needs snapshot so explicit checkbox selections can redistribute.
          this.validationSnapshot = this.buildValidationSnapshot();
        }
        if (this.validationPassed) {
          if (summary) {
            this.toastr.success(summary, "", this.toastOpts);
          }
        } else if (this.proceedAvailable) {
          if (summary) {
            this.toastr.warning(summary, "", this.toastOpts);
          }
        } else if (summary) {
          this.toastr.warning(summary, "", this.toastOpts);
        }
      },
      error: (err: {
        error?: { statusmessage?: string };
        message?: string;
      }) => {
        this.isValidating = false;
        this.clearValidationState();
        /* Prefer API data-dependent statusmessage; fallback when server returns empty (UI-guarded paths). */
        const rawMsg = resolveRedistributionApiMessage(
          err?.error as Record<string, unknown> | undefined,
          err?.message ?? REDISTRIBUTION_API_MESSAGE_FALLBACKS.validationFailed,
        );
        this.toastr.error(rawMsg, "", this.toastOpts);
      },
    });
  }

  /** Maps API extendedattributes.results into validationResults for the UI. */
  private applyValidationFromResponse(data: Record<string, unknown>): void {
    const ext = (data?.["extendedattributes"] ??
      data?.["ExtendedAttributes"]) as Record<string, unknown> | undefined;
    const rows = (ext?.["results"] ?? ext?.["Results"]) as
      | Array<Record<string, unknown>>
      | undefined;

    if (Array.isArray(rows) && rows.length > 0) {
      this.validationResults = rows.map((r) => {
        const pipelinesRaw = r["pipelines"] ?? r["Pipelines"];
        const pipelines = Array.isArray(pipelinesRaw)
          ? pipelinesRaw
              // Explicit ProceedPipeline | null avoids TS2677 on the filter type predicate below.
              .map((p: unknown): ProceedPipeline | null => {
                // SP returns { id, name }; tolerate legacy string-only entries.
                if (typeof p === "string") {
                  const id = p.trim();
                  return id.length > 0 ? { id, name: "" } : null;
                }
                const rec = (p ?? {}) as Record<string, unknown>;
                const id = String(
                  rec["id"] ?? rec["Id"] ?? rec["routeId"] ?? rec["RouteId"] ?? "",
                ).trim();
                const name = String(
                  rec["name"] ??
                    rec["Name"] ??
                    rec["displayName"] ??
                    rec["DisplayName"] ??
                    rec["routeDefinitionName"] ??
                    rec["RouteDefinitionName"] ??
                    "",
                ).trim();
                // API maps SP SourceDisplayName / SENAME → Source; tolerate alias keys from Validate response.
                const source = String(
                  rec["source"] ??
                    rec["Source"] ??
                    rec["sourceDisplayName"] ??
                    rec["SourceDisplayName"] ??
                    rec["sourceName"] ??
                    rec["SourceName"] ??
                    rec["sename"] ??
                    rec["SENAME"] ??
                    "",
                ).trim();
                const target = String(
                  rec["target"] ??
                    rec["Target"] ??
                    rec["destinationDisplayName"] ??
                    rec["DestinationDisplayName"] ??
                    rec["targetName"] ??
                    rec["TargetName"] ??
                    rec["dename"] ??
                    rec["DENAME"] ??
                    "",
                ).trim();
                const messageType = String(
                  rec["messageType"] ??
                    rec["MessageType"] ??
                    rec["messagetype"] ??
                    "",
                ).trim();
                const status = String(
                  rec["status"] ?? rec["Status"] ?? "",
                ).trim();
                if (id.length === 0) {
                  return null;
                }
                // Only set optional fields when present — keeps one stable ProceedPipeline shape.
                const row: ProceedPipeline = { id, name };
                if (source) {
                  row.source = source;
                }
                if (target) {
                  row.target = target;
                }
                if (messageType) {
                  row.messageType = messageType;
                }
                if (status) {
                  row.status = status;
                }
                return row;
              })
              .filter((p): p is ProceedPipeline => p !== null)
          : undefined;
        return {
          id: String(r["id"] ?? r["Id"] ?? ""),
          valid: !!(r["valid"] ?? r["Valid"]),
          message: String(r["message"] ?? r["Message"] ?? "").trim() || undefined,
          source: String(r["source"] ?? r["Source"] ?? "").trim() || undefined,
          requiresProceed: !!(r["requiresProceed"] ?? r["RequiresProceed"]),
          pipelines,
        };
      });
      const allValidFlag = ext?.["allValid"] ?? ext?.["AllValid"];
      const requiresProceedFlag = ext?.["requiresProceed"] ?? ext?.["RequiresProceed"];
      this.validationPassed =
        allValidFlag === true ||
        (this.validationResults.length > 0 &&
          this.validationResults.every((x) => x.valid));
      this.proceedAvailable =
        requiresProceedFlag === true ||
        (this.validationResults.some((x) => x.requiresProceed) && !this.validationPassed);
      this.selectedProceedPipelineIds = {};
      return;
    }

    this.clearValidationState();
  }

  onRedistribution(): void {
    if (!this.selectedRouteId) {
      this.toastr.warning(
        "Select a pipeline before redistributing.",
        "",
        this.toastOpts,
      );
      return;
    }

    if (this.warnIfSelectedPipelineInactive()) {
      return;
    }

    const idLabel = this.getIdLabel();
    if (!this.canRedistribute()) {
      const hint = `Validate ${idLabel}s before redistributing.`;
      this.toastr.warning(hint, "", this.toastOpts);
      return;
    }

    if (!this.deploymentKey?.trim()) {
      this.toastr.warning(
        "Deployment key is required. Select a pipeline with a valid deployment.",
        "",
        this.toastOpts,
      );
      return;
    }

    if (!this.sourceAgentId?.trim()) {
      this.toastr.warning(
        "Source agent is not configured for this pipeline. Cannot redistribute.",
        "",
        this.toastOpts,
      );
      return;
    }

    const claimMode = this.isClaimIdMode();
    const rawField = this.sanitizeNumericIdsInput(
      claimMode
        ? String(this.form.get("claimId")?.value ?? "")
        : String(this.form.get("caseId")?.value ?? ""),
    );
    const controlName = claimMode ? "claimId" : "caseId";
    this.form.get(controlName)?.setValue(rawField, { emitEvent: false });
    const label: "Case ID" | "Claim ID" = claimMode ? "Claim ID" : "Case ID";
    const ids = this.parseCommaSeparatedIds(rawField, label);
    if (ids === null) {
      return;
    }
    if (ids.length === 0) {
      this.toastr.warning(
        claimMode ? "Claim ID is required." : "Case ID is required.",
        "",
        this.toastOpts,
      );
      return;
    }

    const idField = this.applyDedupedIdsToField(controlName, ids);

    const sessionRaw = sessionStorage.getItem("sessionObjectlogin");
    const loggedInUser: string = sessionRaw
      ? String(JSON.parse(sessionRaw)?.userdata?.["userid"] ?? "")
      : "";

    /** One object per ID; never caseId and claimId on the same object. */
    const distribution: Array<{ caseId: string } | { claimId: string }> = claimMode
      ? ids.map((id) => ({ claimId: id }))
      : ids.map((id) => ({ caseId: id }));

    const payload: Record<string, unknown> = {
      interfaceId: this.selectedRouteId,
      distribution,
      caseId: claimMode ? "" : idField,
      claimId: claimMode ? idField : "",
      deployment: this.deploymentKey.trim(),
      messageType: this.messageType,
      username: loggedInUser,
      sourceAgentId: this.sourceAgentId.trim(),
      // Server skips strict validation when Validate returned the proceed path for this snapshot.
      // Enhancement: also override for hard-invalid ID(s) — publish to topic; rule engine qualifies downstream.
      proceedOverride:
        this.proceedAvailable ||
        this.hasHardInvalidPipelineSelections() ||
        this.hasHardInvalidValidationRows(),
    };

    // Proceed path: include checked SP-listed pipelines for additional Service Bus messages.
    const selectedPipelineIdsByObjectId =
      this.buildSelectedPipelineIdsByObjectId();
    if (selectedPipelineIdsByObjectId) {
      payload["selectedPipelineIdsByObjectId"] = selectedPipelineIdsByObjectId;
    }

    // Table is hidden while reason modal is open (!showReasonModal in showPipelineGroupedTable).
    // Defer snapshot/clear to onReasonConfirm so Close restores full table + checkbox selections.
    // this.snapshotPipelineDisplayNamesFromValidation();
    // this.clearProceedPipelineDetailsFromValidation();

    this.pendingPayload = payload;
    this.redistributionReason = "";
    this.showReasonModal = true;
  }

  onReasonConfirm(): void {
    if (!this.pendingPayload) {
      return;
    }
    if (!this.redistributionReason.trim()) {
      this.toastr.warning("Reason is required.", "", this.toastOpts);
      return;
    }
    this.pendingPayload["reason"] = this.redistributionReason.trim();
    // User committed: capture pipeline display names then clear proceed UI (payload already built).
    this.snapshotPipelineDisplayNamesFromValidation();
    this.clearProceedPipelineDetailsFromValidation();
    this.showReasonModal = false;
    this.successItems = [];

    // Keep validation table hidden while the redistribute call runs (clean background under loader).
    this.isRedistributing = true;
    this.ngxLoader.start();
    this.commonService.postRedistributionGetUrl(this.pendingPayload).subscribe({
      next: (getUrlData: Record<string, unknown>) => {
        const getUrlOk = String(getUrlData?.["status"] ?? "") === "Success";
        if (!getUrlOk) {
          this.ngxLoader.stop();
          this.isRedistributing = false;
          this.applyValidationFromResponse(getUrlData);
          const msg = resolveRedistributionApiMessage(
            getUrlData,
            REDISTRIBUTION_API_MESSAGE_FALLBACKS.redistributionFailed,
          );
          this.toastr.error(msg, "", this.toastOpts);
          return;
        }
        this.ngxLoader.stop();
        this.isRedistributing = false;
        this.notifyRedistributionOutcome(getUrlData);
      },
      error: (err: {
        error?: { statusmessage?: string };
        message?: string;
        status?: number;
      }) => {
        this.ngxLoader.stop();
        this.isRedistributing = false;
        const rawMsg = resolveRedistributionApiMessage(
          err?.error as Record<string, unknown> | undefined,
          err?.message ?? REDISTRIBUTION_API_MESSAGE_FALLBACKS.urlLookupFailed,
        );
        const httpCode = err?.status && err.status > 0 ? err.status : null;
        const text = httpCode ? `Error ${httpCode}: ${rawMsg}` : rawMsg;
        this.toastr.error(text, "", this.toastOpts);
      },
    });
  }

  private failurePartHint(part: string): string {
    switch (part.trim()) {
      case "ClaimId":
        return "Check the Claim ID field.";
      case "CaseId":
        return "Check the Case ID field.";
      case "InterfaceHeaders":
        return "Check interface sender headers or credentials.";
      case "OutboundAgent":
        return "The outbound agent rejected this request.";
      case "MessageApi":
        return "The message API did not accept this request.";
      default:
        return "";
    }
  }

  /** Column title for success table first column (matches Case ID vs Claim ID mode). */
  getSuccessCaseOrClaimColumnHeader(): string {
    return this.isClaimIdMode() ? "Claim ID" : "Case ID";
  }

  /** Build one post-redistribute success row; flags input pipeline for highlighted styling. */
  private toSuccessItem(
    caseOrClaimId: string,
    pipelineId: string,
    transactionId: string,
    pipelineNameFromApi?: string,
  ): RedistributionSuccessItem {
    const pid = String(pipelineId ?? "").trim();
    const apiName = String(pipelineNameFromApi ?? "").trim();
    return {
      caseOrClaimId,
      pipelineId: pid,
      pipelineName: apiName || this.resolvePipelineDisplayName(pid),
      transactionId,
      isInputPipeline: this.isInputPipelineId(pid),
    };
  }

  /** Resolve display name for a pipeline GUID (dropdown, validation list, or pre-redistribute snapshot). */
  private resolvePipelineDisplayName(pipelineId: string): string {
    const pid = String(pipelineId ?? "").trim();
    if (!pid) {
      return "—";
    }
    const cached = this.pipelineDisplayNameById[pid.toLowerCase()];
    if (cached?.trim()) {
      return cached.trim();
    }
    if (
      this.selectedRouteId &&
      pid.toLowerCase() === this.selectedRouteId.trim().toLowerCase()
    ) {
      return this.selectedRouteLabel?.trim() || pid;
    }
    for (const row of this.validationResults) {
      for (const pipeline of row.pipelines ?? []) {
        if (pipeline.id.trim().toLowerCase() === pid.toLowerCase()) {
          return pipeline.name?.trim() || pid;
        }
      }
    }
    return pid;
  }

  /**
   * Build success rows from API extendedattributes.redistributionResults (preferred)
   * or fall back to statusmessage txn list + same send order as backend.
   */
  private buildSuccessItemsFromResponse(
    data: Record<string, unknown>,
    statusMsg: string,
  ): RedistributionSuccessItem[] {
    const ext = (data?.["extendedattributes"] ??
      data?.["ExtendedAttributes"]) as Record<string, unknown> | undefined;
    const apiRows = (ext?.["redistributionResults"] ??
      ext?.["RedistributionResults"]) as unknown[] | undefined;

    if (Array.isArray(apiRows) && apiRows.length > 0) {
      const mapped = apiRows
        .map((row: unknown) => {
          const rec = (row ?? {}) as Record<string, unknown>;
          const caseOrClaimId = String(
            rec["objectId"] ?? rec["ObjectId"] ?? "",
          ).trim();
          const pipelineId = String(
            rec["pipelineId"] ?? rec["PipelineId"] ?? "",
          ).trim();
          const transactionId = String(
            rec["transactionId"] ?? rec["TransactionId"] ?? "",
          ).trim();
          const pipelineName = String(
            rec["pipelineName"] ?? rec["PipelineName"] ?? "",
          ).trim();
          if (!caseOrClaimId || !transactionId) {
            return null;
          }
          return this.toSuccessItem(
            caseOrClaimId,
            pipelineId,
            transactionId,
            pipelineName,
          );
        })
        .filter((x): x is RedistributionSuccessItem => x !== null);
      if (mapped.length > 0) {
        return mapped;
      }
    }

    // Fallback when extendedattributes not present (legacy API): match txn order to send loops.
    const claimMode = this.isClaimIdMode();
    const idsRaw = String(
      (claimMode
        ? this.pendingPayload?.["claimId"]
        : this.pendingPayload?.["caseId"]) ?? "",
    );
    const objectIds = idsRaw
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const txnMatch = statusMsg.match(/transactionid\s*-\s*(.+)/i);
    const txnIds = txnMatch
      ? txnMatch[1].split(",").map((s) => s.trim()).filter((s) => s.length > 0)
      : [];
    const selectedByObject = (this.pendingPayload?.[
      "selectedPipelineIdsByObjectId"
    ] ?? this.selectedProceedPipelineIds) as Record<string, string[]> | undefined;

    const items: RedistributionSuccessItem[] = [];
    let txnIndex = 0;
    for (const objectId of objectIds) {
      if (txnIndex < txnIds.length) {
        items.push(
          this.toSuccessItem(
            objectId,
            this.selectedRouteId,
            txnIds[txnIndex++],
          ),
        );
      }
      const extraPipelineIds = selectedByObject?.[objectId] ?? [];
      for (const pipelineId of extraPipelineIds) {
        const pid = String(pipelineId ?? "").trim();
        if (!pid || txnIndex >= txnIds.length) {
          continue;
        }
        if (pid.toLowerCase() === this.selectedRouteId.trim().toLowerCase()) {
          continue;
        }
        items.push(this.toSuccessItem(objectId, pid, txnIds[txnIndex++]));
      }
    }
    if (items.length > 0) {
      return items;
    }

    return statusMsg
      ? [
          {
            caseOrClaimId: "",
            pipelineName: "",
            transactionId: statusMsg,
          },
        ]
      : [];
  }

  /** API outcome feedback: success shown inline via successItems, errors via toastr. */
  private notifyRedistributionOutcome(data: Record<string, unknown>): void {
    const statusOk = String(data?.["status"] ?? "") === "Success";
    const statusMsg = String(data?.["statusmessage"] ?? "").trim();

    if (statusOk) {
      /* Success table uses redistributionResults; statusmessage may be empty after API split. */
      this.successItems = this.buildSuccessItemsFromResponse(data, statusMsg);
      // Card 2 shows success table only — hide prior validation rows, tables, and disclaimer.
      this.clearValidationResultsAfterSuccess();
      return;
    }

    const ext = (data?.["extendedattributes"] ??
      data?.["ExtendedAttributes"]) as Record<string, unknown> | undefined;
    const clientStatusCode = ext?.["clientStatusCode"] ?? ext?.["ClientStatusCode"];
    const failurePart = ext?.["failurePart"] ?? ext?.["FailurePart"];
    const httpStatusCode = ext?.["httpStatusCode"] ?? ext?.["HttpStatusCode"];
    const errorSource = String(ext?.["errorSource"] ?? ext?.["ErrorSource"] ?? "");
    const statusLabel = httpStatusCode ? `Error ${httpStatusCode}` : "";
    const sourceLabel = errorSource ? `in ${errorSource}` : "";
    const prefix = [statusLabel, sourceLabel].filter(Boolean).join(" ");
    const hint =
      clientStatusCode === 406
        ? this.failurePartHint(String(failurePart ?? ""))
        : "";
    /* Server-discovered errors keep API statusmessage; generic fallback when empty. */
    const alertStatusMsg =
      statusMsg || REDISTRIBUTION_API_MESSAGE_FALLBACKS.redistributionFailed;
    const alertText = [prefix, alertStatusMsg, hint]
      .filter((s) => !!String(s).trim())
      .join(" — ");
    this.applyValidationFromResponse(data);
    this.toastr.error(alertText, "", this.toastOpts);
  }

  /**
   * Clears pipeline tables/checkboxes from Card 2 after Redistribute is clicked.
   * Checkbox selections are already captured in pendingPayload before this runs.
   */
  private clearProceedPipelineDetailsFromValidation(): void {
    // Proceed rows removed; valid rows keep status line only (pipelines cleared → no table/checkboxes).
    this.validationResults = this.validationResults
      .filter((row) => !row.requiresProceed)
      .map((row) => ({ ...row, pipelines: undefined }));
    this.selectedProceedPipelineIds = {};
  }

  /** After successful redistribute, remove validation UI so Card 2 shows success results only. */
  private clearValidationResultsAfterSuccess(): void {
    this.validationResults = [];
    this.validationPassed = false;
    this.proceedAvailable = false;
    this.validationSnapshot = "";
    this.selectedProceedPipelineIds = {};
  }

  /**
   * Caches pipeline id → display name before proceed UI rows are removed on Redistribute.
   * Used when API redistributionResults has no PipelineName (legacy) or validation list is cleared.
   */
  private snapshotPipelineDisplayNamesFromValidation(): void {
    const map: Record<string, string> = { ...this.pipelineDisplayNameById };
    const selectedId = this.selectedRouteId?.trim().toLowerCase() ?? "";
    const selectedLabel = this.selectedRouteLabel?.trim() ?? "";
    if (selectedId && selectedLabel) {
      map[selectedId] = selectedLabel;
    }
    for (const row of this.validationResults) {
      for (const pipeline of row.pipelines ?? []) {
        const id = String(pipeline.id ?? "").trim().toLowerCase();
        const name = String(pipeline.name ?? "").trim();
        if (id && name) {
          map[id] = name;
        }
      }
    }
    this.pipelineDisplayNameById = map;
  }

  /** Single-line validation text (avoids duplicate "Valid" / "Proceed required" labels). */
  getValidationStatusText(r: {
    id: string;
    valid: boolean;
    message?: string;
    requiresProceed?: boolean;
  }): string {
    const label = this.getIdLabel();
    if (r.valid) {
      return `${label} ${r.id} is valid for the selected pipeline.`;
    }
    if (r.requiresProceed) {
      let detail =
        String(r.message ?? "").trim() ||
        "No transactions were found for the selected pipeline.";
      // Helper text below the table covers next steps; strip duplicate CTA from status line.
      detail = detail
        .replace(/Click the Redistribute button to proceed\.?/i, "")
        .replace(/Click Proceed to redistribute it\.?/i, "")
        .trim();
      if (!detail) {
        detail = "No transactions were found for the selected pipeline.";
      }
      return `${label} ${r.id} — ${detail}`;
    }
    const detail = String(r.message ?? "").trim();
    const resolvedDetail =
      detail || REDISTRIBUTION_API_MESSAGE_FALLBACKS.rowValidationFailed;
    return resolvedDetail
      ? `${label} ${r.id} — Not valid — ${resolvedDetail}`
      : `${label} ${r.id} — Not valid`;
  }

  /** Table heading for SP/Cosmos pipeline list (valid and proceed paths). */
  getPipelineListTableTitle(): string {
    return `Pipelines with transactions for this ${this.getIdLabel()} (active):`;
  }

  /**
   * Guidance below status line when multiple pipelines exist for this Case/Claim ID.
   * Hidden when only one pipeline is in the list (nothing else to select).
   */
  getPipelineListHelperText(r: {
    valid: boolean;
    requiresProceed?: boolean;
    pipelines?: ProceedPipeline[];
  }): string {
    const pipelines = this.getActiveProceedPipelines(r.pipelines);
    if (pipelines.length <= 1) {
      return "";
    }
    const idLabel = this.getIdLabel().toLowerCase();
    if (r.valid) {
      return (
        `If you wish to redistribute on other pipelines for the same ${idLabel}, ` +
        `select them in the list below. The pipeline chosen in the search box is always included.`
      );
    }
    if (r.requiresProceed) {
      return (
        `If you wish to select other pipelines to redistribute for the same ${idLabel}, ` +
        `select them in the list below. The pipeline chosen in the search box is also included when you redistribute.`
      );
    }
    return "";
  }

  /** CSS class for per-ID validation row in Card 2. */
  getValidationRowClass(r: {
    valid: boolean;
    requiresProceed?: boolean;
  }): string {
    if (r.valid) {
      return "redistribution-result-valid";
    }
    if (r.requiresProceed) {
      return "redistribution-result-proceed";
    }
    return "redistribution-result-invalid";
  }

  // ---------------------------------------------------------------------------
  // Pipeline-grouped table (enhancement): one row per pipeline, IDs column with
  // per-ID checkboxes. Reuses selectedProceedPipelineIds + onProceedPipelineCheck.
  // ---------------------------------------------------------------------------

  /** True when validation row is hard-invalid (no SP pipelines, not proceed path). */
  isHardInvalidValidationRow(r: {
    valid: boolean;
    requiresProceed?: boolean;
  }): boolean {
    return !r.valid && !r.requiresProceed;
  }

  /** True when at least one validated ID is hard-invalid (drives the compact guidance line). */
  hasHardInvalidValidationRows(): boolean {
    return this.validationResults.some((r) => this.isHardInvalidValidationRow(r));
  }

  /** Any checkbox selection in selectedProceedPipelineIds (used to unlock Redistribute). */
  hasExplicitPipelineSelections(): boolean {
    return Object.values(this.selectedProceedPipelineIds).some(
      (ids) => (ids ?? []).length > 0,
    );
  }

  /** True when a hard-invalid ID has at least one extra pipeline checked. */
  hasHardInvalidPipelineSelections(): boolean {
    for (const row of this.validationResults) {
      if (!this.isHardInvalidValidationRow(row)) {
        continue;
      }
      const selected = this.selectedProceedPipelineIds[row.id] ?? [];
      if (selected.length > 0) {
        return true;
      }
    }
    return false;
  }

  /** Show unified pipeline×ID table after Validate (hidden after success). */
  showPipelineGroupedTable(): boolean {
    return (
      // Hide table while reason modal is open OR redistribute is in-flight (clean background); restored on Close.
      !this.showReasonModal &&
      !this.isRedistributing &&
      this.successItems.length === 0 &&
      this.validationResults.length > 0 &&
      this.getPipelineGroupedRows().length > 0
    );
  }

  /**
   * Invert validationResults (ID → pipelines) into pipeline → IDs for the enhanced table.
   * Hard-invalid IDs are attached to every pipeline row so user can force-select a pipeline.
   */
  getPipelineGroupedRows(): PipelineGroupRow[] {
    const pipelineById = new Map<string, ProceedPipeline>();
    const idsByPipelineKey = new Map<string, Map<string, PipelineGroupedIdBinding>>();

    const addPipeline = (p: ProceedPipeline): void => {
      const id = String(p.id ?? "").trim();
      if (!id) {
        return;
      }
      const key = id.toLowerCase();
      if (!pipelineById.has(key)) {
        pipelineById.set(key, p);
        idsByPipelineKey.set(key, new Map());
      }
    };

    const bindIdToPipeline = (
      pipelineId: string,
      binding: PipelineGroupedIdBinding,
    ): void => {
      const key = pipelineId.trim().toLowerCase();
      if (!key) {
        return;
      }
      const idMap = idsByPipelineKey.get(key);
      if (!idMap) {
        return;
      }
      if (!idMap.has(binding.objectId)) {
        idMap.set(binding.objectId, binding);
      }
    };

    // Collect pipelines + ID bindings from valid / proceed validation rows (existing SP data).
    for (const row of this.validationResults) {
      const binding: PipelineGroupedIdBinding = {
        objectId: row.id,
        valid: row.valid,
        requiresProceed: !!row.requiresProceed,
        isHardInvalid: this.isHardInvalidValidationRow(row),
      };
      if (row.valid || row.requiresProceed) {
        for (const p of this.getActiveProceedPipelines(row.pipelines)) {
          addPipeline(p);
          bindIdToPipeline(p.id, binding);
        }
      }
    }

    // Always show the autocomplete input pipeline (existing rule: always included on send).
    const inputId = this.selectedRouteId?.trim() ?? "";
    if (inputId) {
      addPipeline({
        id: inputId,
        name: this.selectedRouteLabel?.trim() || inputId,
        messageType: this.messageType?.trim() || undefined,
      });
      for (const row of this.validationResults) {
        // Enhancement: include hard-invalid IDs on input pipeline (always included on send).
        // Rule engine qualifies downstream; UI shows locked ✓ like valid/proceed IDs.
        bindIdToPipeline(inputId, {
          objectId: row.id,
          valid: row.valid,
          requiresProceed: !!row.requiresProceed,
          isHardInvalid: this.isHardInvalidValidationRow(row),
        });
      }
    }

    // Hard-invalid IDs: no SP list — show on every known pipeline row for optional force-selection.
    const hardInvalidBindings: PipelineGroupedIdBinding[] = this.validationResults
      .filter((r) => this.isHardInvalidValidationRow(r))
      .map((r) => ({
        objectId: r.id,
        valid: false,
        requiresProceed: false,
        isHardInvalid: true,
      }));
    for (const key of pipelineById.keys()) {
      const p = pipelineById.get(key)!;
      for (const binding of hardInvalidBindings) {
        bindIdToPipeline(p.id, binding);
      }
    }

    // Align-with-previous block (commented): hard-invalid IDs were excluded from grouped table.
    // if (this.isHardInvalidValidationRow(row)) { continue; }

    const rows: PipelineGroupRow[] = [];
    for (const [key, pipeline] of pipelineById.entries()) {
      const idMap = idsByPipelineKey.get(key);
      const ids = idMap ? Array.from(idMap.values()) : [];
      if (ids.length === 0) {
        continue;
      }
      rows.push({ pipeline, ids });
    }

    // Input pipeline first, then alphabetical by name (stable scan order).
    rows.sort((a, b) => {
      const aInput = this.isInputPipelineId(a.pipeline.id) ? 0 : 1;
      const bInput = this.isInputPipelineId(b.pipeline.id) ? 0 : 1;
      if (aInput !== bInput) {
        return aInput - bInput;
      }
      return (a.pipeline.name || a.pipeline.id).localeCompare(
        b.pipeline.name || b.pipeline.id,
      );
    });
    return rows;
  }

  /** Helper under the grouped table (same intent as prior per-ID helper text). */
  getPipelineGroupedTableHelperText(): string {
    const rows = this.getPipelineGroupedRows();
    if (rows.length <= 1) {
      return "";
    }
    const idLabel = this.getIdLabel().toLowerCase();
    return (
      `Select ${idLabel}(s) per pipeline below. ` +
      `The pipeline chosen in the search box is always included for each ID. ` +
      `IDs marked invalid can be force-selected on any pipeline; the rule engine qualifies downstream.`
    );
  }

  trackPipelineGroupRow(_index: number, row: PipelineGroupRow): string {
    return row.pipeline.id;
  }

  trackPipelineGroupedId(_index: number, binding: PipelineGroupedIdBinding): string {
    return binding.objectId;
  }

  /** CSS class for one ID chip in the grouped table IDs column. */
  getPipelineGroupedIdChipClass(binding: PipelineGroupedIdBinding): string {
    if (binding.isHardInvalid) {
      return "redistribution-id-chip-invalid";
    }
    if (binding.valid) {
      return "redistribution-id-chip-valid";
    }
    if (binding.requiresProceed) {
      return "redistribution-id-chip-proceed";
    }
    return "";
  }

  /**
   * Input pipeline is always sent for every ID (existing send queue) — show ✓, not a toggle.
   * Other pipelines use selectedProceedPipelineIds (unchanged data structure).
   */
  isIdPipelineSelectionLocked(
    binding: PipelineGroupedIdBinding,
    pipelineId: string,
  ): boolean {
    return this.isInputPipelineId(pipelineId);
  }

  isIdPipelineSelected(
    binding: PipelineGroupedIdBinding,
    pipelineId: string,
  ): boolean {
    if (this.isIdPipelineSelectionLocked(binding, pipelineId)) {
      return true;
    }
    return this.isProceedPipelineChecked(binding.objectId, pipelineId);
  }

  /** Per-ID checkbox in grouped table — delegates to existing proceed toggle. */
  onGroupedIdPipelineToggle(
    binding: PipelineGroupedIdBinding,
    pipelineId: string,
  ): void {
    if (this.isIdPipelineSelectionLocked(binding, pipelineId)) {
      return;
    }
    this.onProceedPipelineToggle(binding.objectId, pipelineId);
  }

  /** Pipeline-row master checkbox: select/deselect all toggleable IDs on this pipeline. */
  isPipelineGroupRowAllSelected(row: PipelineGroupRow): boolean {
    const toggleable = row.ids.filter(
      (b) => !this.isIdPipelineSelectionLocked(b, row.pipeline.id),
    );
    if (toggleable.length === 0) {
      return false;
    }
    return toggleable.every((b) =>
      this.isIdPipelineSelected(b, row.pipeline.id),
    );
  }

  isPipelineGroupRowIndeterminate(row: PipelineGroupRow): boolean {
    const toggleable = row.ids.filter(
      (b) => !this.isIdPipelineSelectionLocked(b, row.pipeline.id),
    );
    if (toggleable.length === 0) {
      return false;
    }
    const checked = toggleable.filter((b) =>
      this.isIdPipelineSelected(b, row.pipeline.id),
    ).length;
    return checked > 0 && checked < toggleable.length;
  }

  onPipelineGroupRowToggle(row: PipelineGroupRow): void {
    const toggleable = row.ids.filter(
      (b) => !this.isIdPipelineSelectionLocked(b, row.pipeline.id),
    );
    if (toggleable.length === 0) {
      return;
    }
    const selectAll = !this.isPipelineGroupRowAllSelected(row);
    for (const binding of toggleable) {
      const currently = this.isIdPipelineSelected(binding, row.pipeline.id);
      if (currently !== selectAll) {
        this.onProceedPipelineCheck(binding.objectId, row.pipeline.id, selectAll);
      }
    }
  }

  onReasonClose(): void {
    this.showReasonModal = false;
    this.pendingPayload = null;
  }

  onReset(): void {
    this.pipelineselectclass = "";
    this.form.reset({ interfaceSearch: "", caseId: "", claimId: "" });
    this.redistributionState.clearState();
    this.showReasonModal = false;
    this.pendingPayload = null;
    this.redistributionReason = "";
    this.successItems = [];
    this.clearValidationState();
    this.clearInterfaceSearch();
  }
}
